/**
 * NewTasksBanner — in-view info banner showing how many new tasks appeared
 * since the user last visited this view.
 *
 * Rendered in ViewShell's `beforeContent` slot.
 * Requires manual dismiss — clearing also removes per-row "new" badges.
 *
 * Visual: horizontal bar with accent left border, "N new tasks since [time]",
 * and a dismiss button.
 */

import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import XIcon from "@/shared/icons/pikaicons/stroke/maths/multiple-cross-cancel-default.svg?react"
import { db } from "@/db/schema"
import type { ViewItem } from "@/features/views/hooks/useViewData"

interface NewTasksBannerProps {
  /** All items currently in the view */
  items: ViewItem[]
  /** Timestamp of the user's previous visit (ms epoch string), or undefined */
  lastSeenAt: string | undefined
  /** Timestamp when the current view session started (ms epoch string) */
  viewOpenedAt: string
  /** Called when the banner is dismissed — clears all per-row new indicators */
  onDismiss?: () => void
}

/**
 * Compute a human-readable relative time like "2 hours ago", "3 days ago".
 */
function relativeTime(timestampMs: number): string {
  const diff = Date.now() - timestampMs
  const minutes = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days = Math.floor(diff / 86_400_000)

  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days === 1) return "yesterday"
  if (days < 7) return `${days}d ago`
  return new Date(timestampMs).toLocaleDateString()
}

export const NewTasksBanner = memo(function NewTasksBanner({
  items,
  lastSeenAt,
  viewOpenedAt,
  onDismiss,
}: NewTasksBannerProps) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(false)

  // Reset dismissed state when lastSeenAt changes (new navigation)
  useEffect(() => {
    setDismissed(false)
  }, [lastSeenAt])

  // Count new tasks — only those created between lastSeenAt and viewOpenedAt,
  // excluding tasks created by the current user (no self-notifications).
  const { newCount, sinceText } = useMemo(() => {
    if (!lastSeenAt) return { newCount: 0, sinceText: "" }

    const currentUserId = db.cloud?.currentUserId

    const count = items.filter(
      (vi) =>
        vi.task.createdAt &&
        vi.task.createdAt > lastSeenAt &&
        vi.task.createdAt < viewOpenedAt &&
        // Skip tasks the current user created themselves
        (!currentUserId || vi.task.owner !== currentUserId),
    ).length

    return {
      newCount: count,
      sinceText: relativeTime(Number(lastSeenAt)),
    }
  }, [items, lastSeenAt, viewOpenedAt])

  const handleDismiss = useCallback(() => {
    setDismissed(true)
    onDismiss?.()
  }, [onDismiss])

  if (newCount === 0 || dismissed) return null

  return (
    <div className="mx-4 mt-1 mb-2 flex items-center gap-2 rounded-lg border-l-4 border-primary bg-primary/5 px-3 py-2 text-sm">
      <span className="flex-1">
        {t("{{count}} new task since {{time}}", {
          count: newCount,
          time: sinceText,
        })}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
        aria-label={t("Dismiss")}
      >
        <XIcon className="size-4" />
      </button>
    </div>
  )
})

/**
 * Check if a task should show a "new" badge.
 * A task is "new" if created after lastSeenAt and before viewOpenedAt,
 * and was NOT created by the current user.
 */
export function isNewTask(
  task: { createdAt?: string; owner?: string },
  lastSeenAt: string | undefined,
  viewOpenedAt: string,
): boolean {
  if (!lastSeenAt || !task.createdAt) return false
  const currentUserId = db.cloud?.currentUserId
  return (
    task.createdAt > lastSeenAt &&
    task.createdAt < viewOpenedAt &&
    (!currentUserId || task.owner !== currentUserId)
  )
}
